/**
 * API «Обзор СПП» (AK-740) — drill-down компания → кластер → хозяйство.
 *
 * Спека: `docs/superpowers/specs/2026-08-23-spp-overview-requirements.md`.
 * Backend: `GET /api/v1/planning/spp-overview` (`backend/src/ikolhoz/api/v1/spp_overview.py`).
 *
 * % — ГЛАВНАЯ метрика (решение founder 23.08): в типах `pct` идёт первым по
 * смыслу поля ячейки. Слово «пашня» в UI не пишем — только числа/подписи.
 */
import { api } from '@/lib/api';

export type SppLevel = 'company' | 'cluster' | 'farm';

export interface SppCell {
  area_ha: number;
  pct: number | null;
  yield_c_ha: number | null;
  /**
   * actual — убрано полностью, урожайность внесена; estimate — убрано
   * частично, оценка по убранной части; growing — ничего не убрано
   * («убирается»); no_data — убрано ПОЛНОСТЬЮ, но урожайность нигде не
   * внесена (это НЕ «growing» — культура уже не в поле, дыра в данных);
   * plan — колонка варианта будущего; none — пусто.
   */
  yield_status: 'actual' | 'estimate' | 'growing' | 'no_data' | 'plan' | 'none';
}

export interface SppRow {
  key: string;
  crop_id: string | null;
  name: string;
  kind: 'crop' | 'fallow';
  /**
   * Подтип «не культуры» из разбора тенант-каталога
   * (`local_catalog_items.not_a_crop_kind`): `field_state` / `land_parcel` /
   * `crop_mix`, `null` — обычное занятие. Тем же признаком чистится легенда
   * карты (AK-547).
   */
  not_a_crop_kind: string | null;
  cells: Record<string, SppCell>;
}

/**
 * Строка — служебное занятие, а не культура (AK-748).
 *
 * «Передано в аренду», «Животноводство», «Пастбище», «Сенокос», «Раскорчевка»,
 * «Услуги по выращ. …» — площадь настоящая, культура — нет. Единственный
 * честный способ их отличить — признак разбора: список имён на фронте не
 * признак, а угадывание (переименуют термин — фильтр молча умрёт).
 *
 * Правило fail-closed: культура — это `null` (термин не размечен) либо
 * `crop_mix` (травосмесь — настоящее занятие поля, карта её красит). Всё
 * остальное, включая подтип, которого сегодня ещё нет, — служебное.
 */
export function isServiceOccupation(row: Pick<SppRow, 'not_a_crop_kind'>): boolean {
  // `?? null` — не косметика: старый ответ (кэш react-query, «зависшая» вкладка
  // до выката бэка) поля не несёт вовсе, и `undefined !== null` объявило бы
  // служебным ВСЁ — экран остался бы без единой культуры.
  const kind = row.not_a_crop_kind ?? null;
  return kind !== null && kind !== 'crop_mix';
}

export interface SppColumn {
  key: string;
  kind: 'fact' | 'future_sum' | 'future_variant';
  year: number | null;
  label: string;
  is_current: boolean;
  scenario_id: string | null;
  scenario_name: string | null;
  is_primary: boolean | null;
  rows_from_fields: boolean;
  /**
   * Только kind='future_sum' — покрытие суммы ТРЕМЯ числами (backend ревью
   * #891 п.4 + повторное ревью head-web #892 п.5: `farms_total` рядом с
   * бейджем «N хозяйств» уровня читались как один и тот же знаменатель, а
   * это были разные множества):
   *   farms_total         — ВСЕ хозяйства уровня (то же множество, что в
   *                          шапке экрана и в знаменателе %);
   *   farms_with_scenario — у скольких из них ЕСТЬ вариант плана на
   *                          будущий сезон;
   *   farms_with_plan     — у скольких из них вариант РАЗЛОЖЕН по полям
   *                          (реально дал строки в сумму).
   * У future_variant/fact — все три null (колонка про одно хозяйство/год).
   */
  farms_with_plan: number | null;
  farms_with_scenario: number | null;
  farms_total: number | null;
}

export interface SppVariantChip {
  scenario_id: string;
  name: string;
  is_primary: boolean;
  is_shown: boolean;
}

export interface SppChild {
  id: string;
  name: string;
  arable_ha: number;
  /**
   * Куда ведёт клик — обычно на кластер, но хозяйство БЕЗ кластера в
   * сайдбаре компании ведёт СРАЗУ на farm (backend ревью #891 п.10: иначе
   * такое хозяйство входит в агрегат/знаменатель, но до него не дойти).
   */
  level: 'cluster' | 'farm';
  sub_count: number;
  sub_label: 'farms' | 'clusters' | 'variants';
}

export interface SppOverview {
  level: SppLevel;
  id: string;
  name: string;
  company_id: string;
  company_name: string;
  cluster_id: string | null;
  cluster_name: string | null;
  arable_ha: number;
  farms_count: number;
  crops_count: number;
  columns: SppColumn[];
  rows: SppRow[];
  totals: Record<string, SppCell>;
  children: SppChild[];
  variants: SppVariantChip[];
}

export interface SppOverviewParams {
  level: SppLevel;
  id: string;
  years?: number[];
  variantIds?: string[];
  futureYear?: number;
}

export function getSppOverview(params: SppOverviewParams): Promise<SppOverview> {
  const qs = new URLSearchParams();
  qs.set('level', params.level);
  qs.set('id', params.id);
  if (params.years && params.years.length > 0) qs.set('years', params.years.join(','));
  if (params.variantIds && params.variantIds.length > 0) {
    qs.set('variant_ids', params.variantIds.join(','));
  }
  if (params.futureYear) qs.set('future_year', String(params.futureYear));
  return api.get<SppOverview>(`/api/v1/planning/spp-overview?${qs.toString()}`);
}

/** «38%» — так, как показываем агроному: без слова «пашня», без десятичных для целых. */
export function formatSppPct(pct: number | null): string {
  if (pct === null) return '—';
  return `${Math.round(pct * 10) / 10}%`;
}

export function formatSppHa(ha: number): string {
  return `${Math.round(ha).toLocaleString('ru-RU')} га`;
}

export function formatSppYield(cell: SppCell): string {
  if (cell.yield_status === 'growing') return 'убирается';
  if (cell.yield_status === 'no_data') return 'нет данных';
  // Колонка варианта будущего — по wireframe (03-farm.html, `yld dash`) это
  // «—», не слово «план» на каждой строке: столбец из повторяющегося слова —
  // шум (ревью head-web #892 п.8).
  if (cell.yield_status === 'plan') return '—';
  if (cell.yield_c_ha === null) return '—';
  const value = `${cell.yield_c_ha.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ц/га`;
  return cell.yield_status === 'estimate' ? `~${value} (оценка)` : value;
}
